import React, { createContext, useContext, useEffect, useReducer } from 'react';

import * as Sentry from '@sentry/react';

import ScriptLoader from './ScriptLoader';

export enum SCRIPTS {
  VERSE = 'verse',
  MATH_JAX = 'mathJax',
  TEAMS_SHARE = 'teamsShare',
  CHARGEBEE = 'chargebee',
  USER_COM = 'userCom',
}

export const scriptUrls = {
  [SCRIPTS.VERSE]: process.env.REACT_APP_VERSE_SCRIPT,
  [SCRIPTS.MATH_JAX]: `${process.env.REACT_APP_IMAGEKIT_URL}react-static-files/${process.env.REACT_APP_BUILD_TYPE}/tex-svg-full.js`,
  [SCRIPTS.TEAMS_SHARE]: 'https://teams.microsoft.com/share/launcher.js',
  [SCRIPTS.CHARGEBEE]: 'https://js.chargebee.com/v2/chargebee.js',
  [SCRIPTS.USER_COM]: 'https://xunlocked.user.com/widget.js',
};

function reducer(state, action) {
  switch (action.type) {
    case SCRIPTS.VERSE:
    case SCRIPTS.MATH_JAX:
    case SCRIPTS.TEAMS_SHARE:
    case SCRIPTS.CHARGEBEE:
    case SCRIPTS.USER_COM:
      return { ...state, [action.type]: action.payload };
    default:
      return state;
  }
}

const initialState = {
  [SCRIPTS.VERSE]: false,
  [SCRIPTS.MATH_JAX]: false,
  [SCRIPTS.TEAMS_SHARE]: false,
  [SCRIPTS.CHARGEBEE]: false,
  [SCRIPTS.USER_COM]: false,
};

const scriptsCtx = createContext<{ state: typeof initialState; dispatch: React.Dispatch<any> }>({
  state: initialState,
  dispatch: () => {},
});

export const ScriptsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { Provider } = scriptsCtx;

  return <Provider value={{ state, dispatch }}>{children}</Provider>;
};

const MOUNTED_SCRIPTS: string[] = [];

function useScriptLoader(type: SCRIPTS) {
  const { state, dispatch } = useContext(scriptsCtx);
  const isLoaded = state[type];

  useEffect(() => {
    const isMounted = MOUNTED_SCRIPTS.includes(type);

    if (!isLoaded && !isMounted && window) {
      // mark as mounted
      MOUNTED_SCRIPTS.push(type);

      const loader = new ScriptLoader(type, scriptUrls[type], Sentry);
      loader.mount(() => dispatch({ type, payload: true }));
    }
  }, [dispatch, isLoaded, type]);

  return isLoaded;
}

export default useScriptLoader;
